'use no memo';
import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import { css } from '@atlaskit/css';
import { useIntl } from 'react-intl';
import isEqual from 'lodash/isEqual';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import Heading from '@atlaskit/heading/heading';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Pagination from '@atlaskit/pagination/pagination';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Anchor from '@atlaskit/primitives/anchor';
import { xcss } from '@atlaskit/primitives/xcss';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { ADFRendererWithExtensions } from '@confluence/adf-renderer/entry-points/ADFRendererWithExtensions.ts';
import {
	BODIED_EXTENSION_TYPE,
	EXTENSION_TYPE,
	INLINE_EXTENSION_TYPE,
	MULTI_BODIED_EXTENSION_TYPE,
	REGULAR_EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/getNavigateToTeamsApp.ts';

import { useDetailsSummaryADFQuery } from './hooks/useDetailsSummaryADFQuery.ts';
import { i18n } from './i18nPagePropertiesReport.ts';
import type {
	DetailsSummaryADFQuery_detailsLines_detailLines,
	DetailsSummaryADFQuery_detailsLines,
} from './queries/__types__/DetailsSummaryADFQuery.ts';
import type {
	GenerateTableBody,
	OptionalTableHeaderParam,
	PagePropertiesReportRendererProps,
	SortOrders,
} from './pprTypes.ts';
import { optionalTableHeaderParams, getADFParser, sortRows } from './utils/index.ts';
import { ErrorState, HeaderPressable } from './ui/index.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRow = styled.tr({
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelsList = styled.ul({
	// TODO: figure out if theres a better way to handle specificity with renderer styles
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	listStyle: 'none !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	paddingLeft: `${token('space.0')} !important`,
});

// Prevents the parent extension-overflow-wrapper's overflowX: 'auto' (editor-common) from
// creating a competing scroll container with TableWrapper, causing a flickering scrollbar.
// 'clip' does not create a new scroll container (unlike 'hidden'), keeping TableWrapper's scroll intact.
const pagePropertiesReportStyles = css({
	overflow: 'clip',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableWrapper = styled.div({
	overflowX: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Table = styled.table({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableBody = styled.tbody({
	borderBottom: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'tr:last-child > td': {
		borderBottom: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeader = styled.th({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	fontWeight: `${token('font.weight.bold')} !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	backgroundColor: 'transparent !important',
	userSelect: 'none',
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		backgroundColor: `${token('color.background.neutral.subtle.hovered')} !important`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableCell = styled.td({
	maxWidth: '500px',
	padding: `${token('space.050')} ${token('space.075')}`,
	verticalAlign: 'top',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionalTableCell = styled(TableCell)({
	minWidth: 'initial',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PaginationWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	minWidth: 0, // prevents pagination <ul> from expanding container width
	padding: `${token('space.100')} ${token('space.200')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'ul:first-of-type': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		marginTop: '0 !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		padding: '0 !important',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div<{ isLoading: boolean }>({
	display: 'grid',
	placeItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: (props) => (props.isLoading ? 'visible' : 'hidden'),
});

const anchorStyles = xcss({
	textDecoration: 'none',
});

const convertPPRRendererProps = (
	isLegacyRenderer: boolean,
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	rendererProps: { contentId: string; contentType?: string; document: any },
) => {
	if (!isLegacyRenderer) {
		return rendererProps;
	}

	return {
		adf: rendererProps.document,
		objectId: rendererProps.contentId,
		contentType: rendererProps.contentType,
	};
};

const hasMacroExtensionNode = (node: any): boolean => {
	if (!node || typeof node !== 'object') {
		return false;
	}

	if (
		(node.type === REGULAR_EXTENSION_TYPE ||
			node.type === BODIED_EXTENSION_TYPE ||
			node.type === INLINE_EXTENSION_TYPE ||
			node.type === MULTI_BODIED_EXTENSION_TYPE) &&
		node.attrs?.extensionType === EXTENSION_TYPE.MACRO_CORE
	) {
		return true;
	}

	return Array.isArray(node.content) && node.content.some(hasMacroExtensionNode);
};

export const PagePropertiesReport = ({
	macroDefaultProps,
	macroParams,
	openAnchorsInNewTab = false,
	Renderer,
	isLegacyRenderer = false,
}: PagePropertiesReportRendererProps): React.JSX.Element => {
	const [sortHeader, setSortHeader] = useState('');
	const [sortOrder, setSortOrder] = useState<SortOrders>();
	const [currentAdfSortHeaderIndex, setCurrentAdfSortHeaderIndex] = useState<number | undefined>();
	const { data, error, isInitialLoad, isPDFExport, loading, pageIndex, paginateTable, totalPages } =
		useDetailsSummaryADFQuery({ macroDefaultProps, macroParams });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { orgId } = useSessionData();
	const { contentId, contentType, spaceId, spaceKey } = macroDefaultProps;

	const optionalColumnsToShow = useMemo(
		() =>
			optionalTableHeaderParams.filter(
				(currentHeaderParam) => macroParams?.[currentHeaderParam]?.value === 'true',
			),
		[macroParams],
	);

	const adfParser = getADFParser({
		contentId,
		createAnalyticsEvent,
	});

	const handleHeaderClick = (header: string, adfHeaderIndex?: number) => {
		setSortHeader(header);
		setSortOrder((prevSortOrder) =>
			// default to ascending sort when changing between sort columns and when first sorting
			header !== sortHeader || prevSortOrder !== 'ascending' ? 'ascending' : 'descending',
		);
		setCurrentAdfSortHeaderIndex(adfHeaderIndex);
	};

	const getSortOrder = (headerName: string) => (sortHeader === headerName ? sortOrder : undefined);

	const getOptionalColumnHeaders = (header: OptionalTableHeaderParam) => {
		switch (header) {
			case 'showCommentsCount':
				return (
					<TableHeader
						aria-sort={getSortOrder('showCommentsCount')}
						key="comments-table-header"
						onClick={() => handleHeaderClick('showCommentsCount')}
					>
						<HeaderPressable sortOrder={getSortOrder('showCommentsCount')}>
							{formatMessage(i18n.comments)}
						</HeaderPressable>
					</TableHeader>
				);
			case 'showCreator':
				return (
					<TableHeader
						aria-sort={getSortOrder('showCreator')}
						key="creator-table-header"
						onClick={() => handleHeaderClick('showCreator')}
					>
						<HeaderPressable sortOrder={getSortOrder('showCreator')}>
							{formatMessage(i18n.owner)}
						</HeaderPressable>
					</TableHeader>
				);
			case 'showLastModified':
				return (
					<TableHeader
						aria-sort={getSortOrder('showLastModified')}
						key="last-modified-table-header"
						onClick={() => handleHeaderClick('showLastModified')}
					>
						<HeaderPressable sortOrder={getSortOrder('showLastModified')}>
							{formatMessage(i18n.modified)}
						</HeaderPressable>
					</TableHeader>
				);
			case 'showLikesCount':
				return (
					<TableHeader
						aria-sort={getSortOrder('showLikesCount')}
						key="reactions-table-header"
						onClick={() => handleHeaderClick('showLikesCount')}
					>
						<HeaderPressable sortOrder={getSortOrder('showLikesCount')}>
							{formatMessage(i18n.reactions)}
						</HeaderPressable>
					</TableHeader>
				);
			case 'showPageLabels':
				return (
					<TableHeader aria-sort={getSortOrder('showPageLabels')} key="labels-table-header">
						<HeaderPressable
							onClick={() => handleHeaderClick('showPageLabels')}
							sortOrder={getSortOrder('showPageLabels')}
						>
							{formatMessage(i18n.labels)}
						</HeaderPressable>
					</TableHeader>
				);
			case 'showUnresolvedCommentsCount':
				return (
					<TableHeader
						aria-sort={getSortOrder('showUnresolvedCommentsCount')}
						key="unresolved-table-header"
						onClick={() => handleHeaderClick('showUnresolvedCommentsCount')}
					>
						<HeaderPressable sortOrder={getSortOrder('showUnresolvedCommentsCount')}>
							{formatMessage(i18n.unresolvedComments)}
						</HeaderPressable>
					</TableHeader>
				);
			default:
				return undefined;
		}
	};

	const generateTableHeaders = ({
		customTitleHeader,
		optionalColumnsToShow,
		tableHeaders,
	}: {
		customTitleHeader?: string;
		optionalColumnsToShow: OptionalTableHeaderParam[];
		tableHeaders: DetailsSummaryADFQuery_detailsLines['renderedHeadings'];
	}) => {
		const titleColumnHeader = (
			<TableHeader
				key="title-table-header"
				aria-sort={getSortOrder('pageTitle')}
				onClick={() => handleHeaderClick('pageTitle')}
			>
				<HeaderPressable sortOrder={getSortOrder('pageTitle')}>
					{customTitleHeader || formatMessage(i18n.title)}
				</HeaderPressable>
			</TableHeader>
		);
		const baseHeaders = tableHeaders.map((headerADF, idx) => {
			return (
				<TableHeader
					key={`${headerADF}-${idx}`}
					data-testid="page-properties-report-table-header"
					aria-sort={getSortOrder(`baseHeader-${idx}`)}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					onClick={() => handleHeaderClick(`baseHeader-${idx}`, idx)}
				>
					<HeaderPressable sortOrder={getSortOrder(`baseHeader-${idx}`)}>
						<ADFRendererWithExtensions
							appearance="full-width"
							document={adfParser(headerADF)}
							spaceId={spaceId}
							spaceKey={spaceKey}
						/>
					</HeaderPressable>
				</TableHeader>
			);
		});

		const optionalHeaders = optionalColumnsToShow
			.map(getOptionalColumnHeaders)
			.filter((header) => header !== undefined);

		return [titleColumnHeader, ...baseHeaders, ...optionalHeaders];
	};

	const getOptionalCells = useCallback(
		(
			header: OptionalTableHeaderParam,
			rowContent: DetailsSummaryADFQuery_detailsLines_detailLines,
			rowNumber: number,
		) => {
			switch (header) {
				case 'showCommentsCount':
					return (
						<OptionalTableCell
							data-testid="page-properties-report-comment-cell"
							key={`comment-cell-row-${rowNumber}`}
						>
							{rowContent.commentsCount > 0 && rowContent.commentsCount}
						</OptionalTableCell>
					);
				case 'showCreator':
					return (
						<OptionalTableCell
							data-testid="page-properties-report-owner-cell"
							key={`creator-cell-row-${rowNumber}`}
						>
							{rowContent?.creator?.accountId && rowContent?.creator?.name && (
								<Anchor
									href={
										getNavigateToTeamsApp(orgId ?? '', {
											type: 'USER',
											payload: {
												userId: rowContent.creator.accountId,
											},
										}).href
									}
									{...(openAnchorsInNewTab && {
										target: '_blank',
										rel: 'noopener noreferrer',
									})}
									xcss={anchorStyles}
								>
									{rowContent.creator.name}
								</Anchor>
							)}
						</OptionalTableCell>
					);
				case 'showLastModified':
					return (
						<OptionalTableCell
							data-testid="page-properties-report-modified-cell"
							key={`last-modified-date-cell-row-${rowNumber}`}
						>
							{rowContent.lastModified?.friendlyModificationDate}
						</OptionalTableCell>
					);
				case 'showLikesCount':
					return (
						<OptionalTableCell
							data-testid="page-properties-reactions-cell"
							key={`reactions-cell-row-${rowNumber}`}
						>
							{rowContent.reactionsCount > 0 && rowContent.reactionsCount}
						</OptionalTableCell>
					);
				case 'showPageLabels':
					return (
						<OptionalTableCell
							data-testid="page-properties-report-labels-cell"
							key={`labels-cell-row-${rowNumber}`}
						>
							<LabelsList>
								{rowContent.labels.map(
									(label, idx) =>
										label?.urlPath &&
										label?.name && (
											<li key={`${label.urlPath}-${idx}`}>
												<Lozenge>
													<Anchor
														href={`/wiki${label.urlPath}`}
														{...(openAnchorsInNewTab && {
															target: '_blank',
															rel: 'noopener noreferrer',
														})}
														xcss={anchorStyles}
													>
														{label.name}
													</Anchor>
												</Lozenge>
											</li>
										),
								)}
							</LabelsList>
						</OptionalTableCell>
					);
				case 'showUnresolvedCommentsCount':
					return (
						<OptionalTableCell
							data-testid="page-properties-report-unresolved-comment-cell"
							key={`unresolved-comment-row-${rowNumber}`}
						>
							{rowContent.unresolvedCommentsCount > 0 && rowContent.unresolvedCommentsCount}
						</OptionalTableCell>
					);
				default:
					return undefined;
			}
		},
		[openAnchorsInNewTab, orgId],
	);

	const generateOptionalBodyCells = useCallback(
		(
			optionalColumnsToShow: OptionalTableHeaderParam[],
			rowContent: DetailsSummaryADFQuery_detailsLines_detailLines,
		) =>
			optionalColumnsToShow.map((column, rowNumber) =>
				getOptionalCells(column, rowContent, rowNumber),
			),
		[getOptionalCells],
	);

	const generateTableBody = useCallback(
		({
			tableContent,
			optionalColumnsToShow,
			sortHeader,
			sortOrder,
			currentAdfSortHeaderIndex,
		}: GenerateTableBody) => {
			if (!tableContent) {
				return;
			}

			if (tableContent.length === 0) {
				return (
					<TableRow>
						<TableCell data-testid="page-properties-report-no-results-cell">
							{formatMessage(i18n.noContent)}
						</TableCell>
					</TableRow>
				);
			}
			return sortRows({
				rows: tableContent,
				sortOrder,
				sortHeader,
				adfHeaderIndex: currentAdfSortHeaderIndex,
				createAnalyticsEvent,
				contentId,
			}).map((reportBodyRow, idx) => {
				if (!reportBodyRow) {
					return;
				}

				return (
					<TableRow key={`${reportBodyRow.id}-row-${idx}`}>
						<TableCell
							data-testid="page-properties-report-title-cell"
							key={`title-table-cell-${reportBodyRow.relativeLink}`}
						>
							<Anchor
								href={`/wiki${reportBodyRow.relativeLink}`}
								{...(openAnchorsInNewTab && {
									target: '_blank',
									rel: 'noopener noreferrer',
								})}
								xcss={anchorStyles}
							>
								{reportBodyRow.title}
							</Anchor>
						</TableCell>
						{reportBodyRow.details.map((reportBodyCol, idx) => {
							const reportBodyRowContentId = reportBodyRow.id.toString();
							const parsedReportBodyCol = reportBodyCol ? adfParser(reportBodyCol) : undefined;
							const shouldUseParentRenderer = Boolean(
								Renderer &&
								parsedReportBodyCol &&
								expValEquals('page_properties_connect_to_forge_macro_render', 'isEnabled', true) &&
								hasMacroExtensionNode(parsedReportBodyCol),
							);
							const CellRenderer = shouldUseParentRenderer ? Renderer : ADFRendererWithExtensions;
							const cellRendererProps = shouldUseParentRenderer
								? convertPPRRendererProps(isLegacyRenderer, {
										contentId: reportBodyRowContentId,
										contentType,
										document: parsedReportBodyCol,
									})
								: {
										contentId: reportBodyRowContentId,
										document: parsedReportBodyCol,
									};
							return (
								<TableCell key={`${reportBodyCol}-cell-${idx}`}>
									{reportBodyCol && (
										<RendererActionsContext>
											<CellRenderer
												{...cellRendererProps}
												appearance="full-width"
												spaceId={spaceId}
												spaceKey={spaceKey}
											/>
										</RendererActionsContext>
									)}
								</TableCell>
							);
						})}
						{generateOptionalBodyCells(optionalColumnsToShow, reportBodyRow)}
					</TableRow>
				);
			});
		},
		[
			adfParser,
			contentType,
			createAnalyticsEvent,
			contentId,
			formatMessage,
			generateOptionalBodyCells,
			isLegacyRenderer,
			openAnchorsInNewTab,
			Renderer,
			spaceId,
			spaceKey,
		],
	);

	const tableRows = useMemo(
		() =>
			generateTableBody({
				tableContent: data?.detailsLines?.detailLines as GenerateTableBody['tableContent'],
				optionalColumnsToShow,
				sortHeader,
				sortOrder,
				currentAdfSortHeaderIndex,
			}),
		[
			data?.detailsLines?.detailLines,
			generateTableBody,
			optionalColumnsToShow,
			sortHeader,
			sortOrder,
			currentAdfSortHeaderIndex,
		],
	);

	if (isInitialLoad) {
		return <Heading size="medium">{formatMessage(i18n.generatingContentPropertiesReport)}</Heading>;
	}

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}

		return (
			<ErrorDisplay error={error}>
				<ErrorState />
			</ErrorDisplay>
		);
	}

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="page-properties-report"
			data-testid="page-properties-report-macro-wrapper"
			css={pagePropertiesReportStyles}
		>
			<TableWrapper>
				<Table>
					<thead data-testid="page-properties-report-table-head">
						<tr>
							{generateTableHeaders({
								tableHeaders: (data?.detailsLines?.renderedHeadings ||
									[]) as DetailsSummaryADFQuery_detailsLines['renderedHeadings'],
								optionalColumnsToShow,
								customTitleHeader: macroParams?.firstcolumn?.value,
							})}
						</tr>
					</thead>
					<TableBody data-testid="page-properties-report-table-body">{tableRows}</TableBody>
				</Table>
			</TableWrapper>
			{totalPages > 1 && !isPDFExport && (
				<PaginationWrapper>
					<Pagination
						onChange={paginateTable}
						pages={Array.from({ length: totalPages }, (_, i) => i + 1)}
						selectedIndex={pageIndex}
						testId="page-properties-report-pagination"
					/>
					<SpinnerWrapper
						isLoading={loading}
						data-testid="page-properties-report-pagination-spinner"
					>
						<Spinner interactionName="page-properties-report-pagination" />
					</SpinnerWrapper>
				</PaginationWrapper>
			)}
		</div>
	);
};

export const PagePropertiesReportRenderer: React.MemoExoticComponent<
	(props: PagePropertiesReportRendererProps) => React.JSX.Element
> = React.memo(
	(props: PagePropertiesReportRendererProps): React.JSX.Element => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const fireRenderingError = (error: Error) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'error',
					actionSubject: 'pagePropertiesReport',
					source: 'PagePropertiesReportRenderer',
					objectId: props.macroDefaultProps.contentId,
					attributes: {
						error: error.message,
						stack: error.stack,
					},
				},
			}).fire();
		};

		return (
			<GenericErrorBoundary
				onError={fireRenderingError}
				renderOnError={() => <ErrorState />}
				attribution={Attribution.PAGE_EXTENSIONS}
			>
				<PagePropertiesReport {...props} />
			</GenericErrorBoundary>
		);
	},
	// we want to minimize rerenders due to shallow prop comparisons bc of how expensive it is to rerender so many renderer components
	(prevProps, nextProps) => isEqual(prevProps, nextProps),
);
